import { isPlaceholderCreate } from "../../shared.ts";
import type { GetNodeTitle } from "../../state/getNodeTitle.ts";
import { resolveNodeProfileCreate, withProfileChildNodes } from "../../state/nodePublicApi.ts";
import type { DesignerNode, GetManifestByTypeId, NodeDesignerDefinitionReference } from "../../types/index.ts";
import type { CreateNode } from "./shared.ts";

export const applyDefinitionCreate = (
  createNode: CreateNode,
  getManifest: GetManifestByTypeId,
  getNodeTitle?: GetNodeTitle,
) => {
  const isPlaceholder = isPlaceholderCreate(getManifest);
  // nodes being created are not in state yet, so profile resolves by object
  const resolveNodeProfile = resolveNodeProfileCreate({ getManifest });

  const applyDefinition = (node: DesignerNode, definitionId: string) => {
    const { typeId, config: initialConfig, childNodes: initialChildNodes, role: initialRole } = node;
    const {
      designer: { definitions },
    } = getManifest(typeId);
    if (!definitions) {
      throw new Error(`Node of type ${typeId} has no definitions`);
    }
    const definition = definitions[definitionId];
    if (definitionId && !definition) {
      throw new Error(`Definition not found on node type: ${typeId} with id ${definitionId}`);
    }

    const config = { ...initialConfig, ...definition.config };
    const role = definition.role ?? initialRole;

    const createNodeFromReference = (
      name: string,
      ref: NodeDesignerDefinitionReference,
      index?: number,
    ): DesignerNode => {
      const createOptions = typeof ref.createOptions === "object" ? ref.createOptions : {};
      let designerNodeWithPlaceholder = createNode(ref.typeId, createOptions);
      if (ref.definitionId) {
        designerNodeWithPlaceholder = applyDefinition(designerNodeWithPlaceholder, ref.definitionId);
      }
      const originalEntry = initialChildNodes[name];
      if (originalEntry) {
        let original: DesignerNode | undefined;
        if (Array.isArray(originalEntry)) {
          if (index === undefined) {
            throw new Error("createNodeFromReference called on multi-value without index.");
          }
          original = originalEntry[index];
        } else {
          original = originalEntry;
        }
        if (original && isPlaceholder(original.typeId)) {
          designerNodeWithPlaceholder = {
            ...designerNodeWithPlaceholder,
            placeholder: original,
          };
        }
      }
      return designerNodeWithPlaceholder;
    };

    const childNodes =
      definition && definition.childNodes
        ? Object.entries(definition.childNodes).reduce<DesignerNode["childNodes"]>((result, [name, refOrRefs]) => {
            result[name] = Array.isArray(refOrRefs)
              ? refOrRefs.map((ref, index) => createNodeFromReference(name, ref, index))
              : refOrRefs
                ? createNodeFromReference(name, refOrRefs)
                : undefined;
            return result;
          }, initialChildNodes)
        : initialChildNodes;

    const appliedNode = { ...node, config, childNodes, role };
    return Object.assign(appliedNode, {
      title: getNodeTitle?.(withProfileChildNodes(appliedNode, resolveNodeProfile)) ?? node.title,
    });
  };

  return applyDefinition;
};

export type ApplyDefinition = ReturnType<typeof applyDefinitionCreate>;
